import { FC, useRef, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { GarantieReferentielDto, RolePrimeDerivee } from 'api/guarantee';
import { guaranteesAPI } from 'services/swagger-api';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { useDialog } from 'hooks/useDialog';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { FormikDropdown } from 'components/forms/formik/FormikDropdown';
import { FormikSwitch } from 'components/forms/formik/FormikSwitch';
import { FormField } from 'components/forms/FormField';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import {
  DERIVED_PREMIUM_ROLE_OPTIONS,
  formatRate,
  isDerivedRole,
  rateOfRole,
} from '../guaranteeLabels';
import { Branch } from '../useBranches';

interface GuaranteeFormValues {
  branchId: string;
  code: string;
  shortLabel: string;
  label: string;
  derivedPremiumRole: RolePrimeDerivee;
  damage: boolean;
}

const validationSchema = Yup.object().shape({
  branchId: Yup.string().required('La branche est requise'),
  code: Yup.string().trim().required('Le code est requis'),
  shortLabel: Yup.string().trim().required('Le libellé court est requis'),
  label: Yup.string().trim().required('Le libellé est requis'),
});

interface GuaranteeReferentialModalProps {
  /** Absent en création. */
  guarantee?: GarantieReferentielDto;
  /** Branche présélectionnée ; vide depuis l'écran des garanties sans branche. */
  branchId?: string;
  branches: Branch[];
  onSuccess: () => void;
}

/**
 * Création et modification d'une garantie du référentiel.
 *
 * La branche reste modifiable en édition : c'est par ce champ qu'une garantie héritée de
 * la reprise, restée sans branche, se voit rattachée.
 */
export const GuaranteeReferentialModal: FC<GuaranteeReferentialModalProps> = ({
  guarantee,
  branchId,
  branches,
  onSuccess,
}) => {
  const formikRef = useRef<FormikProps<GuaranteeFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(guarantee);

  const initialValues: GuaranteeFormValues = {
    branchId: guarantee?.branchId ?? branchId ?? '',
    code: guarantee?.code ?? '',
    shortLabel: guarantee?.shortLabel ?? '',
    label: guarantee?.label ?? '',
    derivedPremiumRole: guarantee?.derivedPremiumRole ?? RolePrimeDerivee.None,
    damage: guarantee?.damage ?? false,
  };

  const findBranch = (id: string) => branches.find((branch) => branch.id === id);

  const handleSubmit = async (
    values: GuaranteeFormValues,
    { setFieldError, setFieldTouched }: FormikHelpers<GuaranteeFormValues>
  ) => {
    if (isSubmitting) return;

    // Le rôle n'est saisi que si la branche calcule les primes dérivées : sinon le champ
    // n'est pas affiché et la garantie garde le rôle qu'elle avait.
    const roleIsEditable = Boolean(findBranch(values.branchId)?.derivedPremiumsEnabled);

    setIsSubmitting(true);
    try {
      const payload = {
        branchId: values.branchId,
        code: values.code.trim(),
        shortLabel: values.shortLabel.trim(),
        label: values.label.trim(),
        derivedPremiumRole: roleIsEditable
          ? values.derivedPremiumRole
          : initialValues.derivedPremiumRole,
        damage: values.damage,
      };

      const response = isEditMode
        ? await guaranteesAPI.updateReferentialGuarantee({
            garantieId: guarantee?.id as string,
            updateGarantieReferentielCommand: payload,
          })
        : await guaranteesAPI.createReferentialGuarantee({
            createGarantieReferentielCommand: payload,
          });

      if (response.isSuccess) {
        toast.success(isEditMode ? 'Garantie modifiée' : 'Garantie créée');
        onSuccess();
        closeDialog();
      } else {
        toast.error(response.errorMessage || "L'enregistrement a échoué");
      }
    } catch (error) {
      console.error('Enregistrement de la garantie impossible', error);
      // Le back refuse un second rôle dérivé identique dans la branche : son message
      // nomme la garantie qui porte déjà ce rôle, il s'affiche sous le champ.
      const { message } = await parseApiError(error);
      if (message && roleIsEditable && isDerivedRole(values.derivedPremiumRole)) {
        setFieldTouched('derivedPremiumRole', true, false);
        setFieldError('derivedPremiumRole', message);
      } else {
        toast.error(message || "L'enregistrement a échoué");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        const selectedBranch = findBranch(values.branchId);

        return (
          <ModalShell
            title={`${
              isEditMode ? guarantee?.label || 'Modifier la garantie' : 'Créer une garantie'
            }${selectedBranch?.code ? ` · Branche ${selectedBranch.code}` : ''}`}
            subtitle="Référentiel des garanties"
            submit={{
              label: 'Sauvegarder',
              onClick: () => formikRef.current?.submitForm(),
              disabled: isSubmitting,
            }}
          >
            <Form className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5">
              <FormikDropdown
                name="branchId"
                label="Branche"
                required
                items={branches.map((branch) => ({
                  key: branch.id,
                  label: branch.label,
                  trailingLabel: branch.code,
                }))}
                placeholder="Sélectionner une branche"
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormikInputText name="code" label="Code" required placeholder="ex. DDE" />
                <FormikInputText
                  name="shortLabel"
                  label="Libellé court"
                  required
                  placeholder="ex. Dégâts des eaux"
                />
              </div>

              <FormikInputText
                name="label"
                label="Libellé"
                required
                placeholder="Intitulé complet affiché au souscripteur"
              />

              {selectedBranch?.derivedPremiumsEnabled && (
                <div className="border-t border-gray-100 pt-4">
                  <DerivedPremiumRoleField branch={selectedBranch} />
                </div>
              )}

              <div className="border-t border-gray-100 pt-4">
                <FormikSwitch name="damage" label="Garantie de dommages" />
              </div>
            </Form>
          </ModalShell>
        );
      }}
    </Formik>
  );
};

/**
 * Rôle dans les primes dérivées (Cat Nat, Cat Tech, Attentat) : cinq choix exclusifs,
 * présentés en boutons radio. N'est rendu que pour une branche qui calcule ces primes ;
 * le taux affiché à côté d'un rôle dérivé est celui de la branche.
 */
const DerivedPremiumRoleField: FC<{ branch: Branch }> = ({ branch }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext<GuaranteeFormValues>();
  const error = touched.derivedPremiumRole ? errors.derivedPremiumRole : undefined;

  return (
    <FormField
      label="Rôle dans les primes dérivées (Cat Nat, Cat Tech, Attentat)"
      name="derivedPremiumRole"
      error={error}
    >
      <p className="text-xs text-gray-400">
        Un seul rôle par garantie. Les trois primes dérivées sont calculées sur la même
        assiette.
      </p>
      <div className="flex flex-col gap-2">
        {DERIVED_PREMIUM_ROLE_OPTIONS.map((option) => {
          const isSelected = values.derivedPremiumRole === option.value;
          const rate = rateOfRole(option.value, branch);

          return (
            <label
              key={option.value}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2 transition-colors ${
                isSelected
                  ? 'border-primary bg-primary/[0.06] ring-1 ring-primary'
                  : 'border-gray-200 hover:border-primary/40 hover:bg-gray-50'
              }`}
            >
              <input
                type="radio"
                name="derivedPremiumRole"
                value={option.value}
                checked={isSelected}
                onChange={() => setFieldValue('derivedPremiumRole', option.value)}
                className="mt-1 h-4 w-4 flex-shrink-0 accent-primary"
              />
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-zinc-800">{option.label}</span>
                  {rate !== undefined && (
                    <span className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">
                      {formatRate(rate)} de l'assiette
                    </span>
                  )}
                </span>
                <span className="block text-xs leading-tight text-gray-400">{option.hint}</span>
              </span>
            </label>
          );
        })}
      </div>
    </FormField>
  );
};
